import React, { ReactNode, FC, useMemo, InputHTMLAttributes, useContext } from 'react';

import { ThemeContext } from 'styled-components';
import filterChildrenByType from '../../../utils/components/_filterChildrenByType';
import excludeChildrenByType from '../../../utils/components/_excludeChildrenByType';

import InlineMessage, { InlineMessageProps } from '../InlineMessage';

import { Content, Action } from './InlineErrorMessage.compounds';

export interface InlineErrorMessageProps extends InlineMessageProps, InputHTMLAttributes<HTMLInputElement> {
  /** Custom error message. Alternative to using the `error`prop. */
  children?: ReactNode;
  /** Error message. If an object, it should contain a `message` prop. */
  error?: ReactNode | { message: ReactNode } | ReactNode[];
}

export type InlineErrorMessageStatics = {
  Content: typeof Content;
  Action: typeof Action;
};

export interface InlineErrorMessageMock {}

const InlineErrorMessage: FC<InlineErrorMessageProps> & InlineErrorMessageStatics = ({ children, error, ...props }) => {
  const theme = useContext(ThemeContext);
  const content = useMemo(() => excludeChildrenByType(children, Action), [children]);
  const action = useMemo(() => filterChildrenByType(children, Action), [children]);

  const errorMessage: ReactNode = useMemo(() => {
    if (typeof error === 'string') {
      return error;
    }

    if (typeof error === 'object' && error !== null && 'message' in error) {
      return error.message;
    }

    return content;
  }, [content, error]);

  return (
    <InlineMessage
      role="alert"
      Icon={theme?.icons?.error}
      color={theme.colors.error}
      background={theme.colors.errorBackground}
      {...props}
    >
      <InlineMessage.Content>
        {errorMessage}
      </InlineMessage.Content>
      {action.length ? <InlineMessage.Action>{action}</InlineMessage.Action> : null}
    </InlineMessage>
  );
};

InlineErrorMessage.Content = Content;
InlineErrorMessage.Action = Action;

export default InlineErrorMessage;
